/**
 * PayPalCheckoutButton — drop-in alternative to the Stripe checkout flow.
 *
 * Mounts the PayPal SDK once per visit (lazy-fetches the client_id from the
 * backend), then renders the official <PayPalButtons /> next to the Stripe
 * button. Both flows share the same shipping address / service inputs.
 *
 * Two backend calls:
 *   1. POST /api/paypal/create-order — returns paypal_order_id
 *   2. POST /api/paypal/capture-order/{id} — finalize, runs same post-paid
 *      side-effects as Stripe (mark sold, Royal Mail label, Stripe Connect
 *      transfer to the seller)
 */
import React, { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { axios, API, logError } from "../lib/api";
import { toast } from "sonner";

export const PayPalCheckoutButton = ({
  listing, serviceId, shippingAddress, total, onSuccess, disabled,
}) => {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/paypal/client-id`).then((r) => {
      if (!cancelled) setConfig(r.data);
    }).catch((e) => logError("paypal client-id fetch", e));
    return () => { cancelled = true; };
  }, []);

  if (!config) return null;
  if (!config.configured) {
    return (
      <div className="text-[11px] text-center text-gray-400 mt-2" data-testid="paypal-not-configured">
        PayPal is not yet enabled. Use the Stripe button above.
      </div>
    );
  }

  return (
    <div className="mt-3" data-testid="paypal-checkout-wrapper">
      <div className="flex items-center gap-3 my-2" aria-hidden="true">
        <span className="flex-1 h-px bg-gray-300" />
        <span className="text-xs uppercase tracking-wide text-gray-500 font-bold">or</span>
        <span className="flex-1 h-px bg-gray-300" />
      </div>

      <PayPalScriptProvider
        options={{
          "client-id": config.client_id,
          currency: config.currency || "GBP",
          intent: "capture",
          components: "buttons",
        }}
      >
        <PayPalButtons
          style={{ layout: "horizontal", color: "blue", shape: "pill", label: "paypal", tagline: false, height: 45 }}
          disabled={disabled}
          forceReRender={[total, serviceId]}
          createOrder={async () => {
            try {
              const res = await axios.post(`${API}/paypal/create-order`, {
                listing_id: listing.id,
                origin_url: window.location.origin,
                shipping_service: serviceId,
                shipping_address: shippingAddress,
              });
              return res.data.paypal_order_id;
            } catch (err) {
              const msg = err.response?.data?.detail || "Could not start PayPal checkout";
              toast.error(msg);
              throw err;
            }
          }}
          onApprove={async (data) => {
            try {
              const res = await axios.post(`${API}/paypal/capture-order/${data.orderID}`);
              if (res.data?.captured || res.data?.already_captured) {
                toast.success("Payment received — thanks!");
                if (onSuccess) onSuccess(res.data.order_id);
              }
            } catch (err) {
              toast.error(err.response?.data?.detail || "PayPal capture failed");
              logError("paypal capture", err);
            }
          }}
          onError={(err) => {
            logError("paypal sdk", err);
            toast.error("PayPal could not load. Try the Stripe option above.");
          }}
        />
      </PayPalScriptProvider>
    </div>
  );
};
